import { GRADIENT_TYPE_IDS, gradientToLut } from '../core/gradient';
import { getPattern } from '../core/patterns';
import type { FillContent, Point } from '../core/types';
import type { RenderTarget } from '../gl/renderTarget';
import type { Texture } from '../gl/texture';
import type { Engine } from './engine';
import type { DocumentResources } from './resources';

/** Endpoints of a gradient that crosses the canvas at `angle` and `scale`. */
export function gradientEndpoints(
  width: number,
  height: number,
  angleDegrees: number,
  scalePercent: number,
): { start: Point; end: Point } {
  const angle = (angleDegrees * Math.PI) / 180;
  const half = (Math.hypot(width, height) / 2) * (Math.max(1, scalePercent) / 100);
  const cx = width / 2;
  const cy = height / 2;
  const dx = Math.cos(angle) * half;
  const dy = Math.sin(angle) * half;
  return {
    start: { x: cx - dx, y: cy - dy },
    end: { x: cx + dx, y: cy + dy },
  };
}

/**
 * Paints a fill layer into a freshly acquired colour target. The caller owns the
 * result and must release it after blending, the same way an adjustment pass
 * does.
 */
export function renderFillLayer(
  engine: Engine,
  resources: DocumentResources,
  fill: FillContent,
  width: number,
  height: number,
): RenderTarget {
  const target = resources.buffers.color.acquire();

  if (fill.kind === 'solid') {
    const { color } = fill;
    target.clear(color.r, color.g, color.b, color.a);
    return target;
  }

  // Gradient and pattern both blend over a base; writing into that base would be
  // a feedback loop, so they paint over a cleared spare that is released after.
  const empty = resources.buffers.color.acquire();
  empty.clear(0, 0, 0, 0);

  if (fill.kind === 'gradient') {
    const lutKey = `fill-grad:${JSON.stringify(fill.gradient)}`;
    const lut = engine.lutTexture(lutKey, () => gradientToLut(fill.gradient));
    if (lut) {
      const { start, end } = gradientEndpoints(width, height, fill.angle, fill.scale);
      engine.renderer.drawGradient(target.framebuffer, empty.texture, null, {
        type: GRADIENT_TYPE_IDS[fill.type],
        start,
        end,
        lut,
        opacity: 1,
        blendMode: 'normal',
        reverse: fill.reverse,
        dither: true,
      });
    } else {
      target.clear(0, 0, 0, 0);
    }
    resources.buffers.color.release(empty);
    return target;
  }

  const pattern = getPattern(fill.patternId);
  const tile =
    pattern && engine.patternTexture(pattern.id, pattern.width, pattern.height, pattern.data);
  if (pattern && tile) {
    const scale = Math.max(1, fill.scale) / 100;
    // The shader samples by document pixels against `uTileSize`, so scaling the
    // reported tile size is enough — the texture itself stays at native resolution.
    engine.renderer.applyPattern(
      target.framebuffer,
      empty.texture,
      engine.renderer.whiteMaskTexture,
      tile,
      {
        docWidth: width,
        docHeight: height,
        originX: 0,
        originY: 0,
        opacity: 1,
        blendMode: 'normal',
        tileWidth: pattern.width * scale,
        tileHeight: pattern.height * scale,
      },
    );
  } else {
    target.clear(0, 0, 0, 0);
  }
  resources.buffers.color.release(empty);
  return target;
}

/** Convenience when a caller only needs the texture. */
export function fillLayerTexture(
  engine: Engine,
  resources: DocumentResources,
  fill: FillContent,
  width: number,
  height: number,
): { texture: Texture; owner: RenderTarget } {
  const owner = renderFillLayer(engine, resources, fill, width, height);
  return { texture: owner.texture, owner };
}
